import { useEffect, useState } from "react";
import { useShallow } from "zustand/react/shallow";

import {
  hasOrgGeoColumns,
  withVisibleOrgGeoColumns
} from "utils/column/withVisibleOrgGeoColumns";

import useDebounce from "hooks/useDebounce";
import useFeatureFlags, { getFieldGroupFlag } from "hooks/useFeatureFlags";

import { IGroupByListColumn, withoutShapefileBoundaryColumns } from "models";

import { useVisibleOrgGeoJobNames } from "components/org-settings/hooks/useVisibleOrgGeoJobNames";

import { useGroupByStore } from "../GroupByStoreProvider";
import { selectColumnFilterDeps } from "../groupBySelectors";
import {
  authorizeColumnsByForecast,
  filterByMetadata,
  filterByProduct,
  filterByText,
  filterByType
} from "../helpers";
import useSelectedCategory from "./useSelectedCategory";

/**
 * Returns a filtered list of columns after applying:
 * - data-type filter
 * - product filter
 * - text filter
 * - authorization (enabling disabling based on forecast toggle)
 */
const useColumns = (): IGroupByListColumn[] => {
  const [list, setList] = useState<Maybe<IGroupByListColumn[]>>(null);
  const {
    categoryList,
    filterText,
    isFiltering,
    props,
    selectedCategory,
    selectedProduct,
    selectedMetadataFilters,
    isForecastToggleOn
  } = useGroupByStore(useShallow(selectColumnFilterDeps));

  const { hasProductFields } = useSelectedCategory();
  const { hasFeature } = useFeatureFlags();
  // Only orgs holding org-geo columns pay for the job queries.
  const visibleJobNames = useVisibleOrgGeoJobNames(hasOrgGeoColumns(categoryList));

  // Fuzzy matching runs synchronously on the main thread per keystroke —
  // debounce so typing stays responsive instead of re-scanning the full
  // field catalogue on every character.
  const debouncedFilterText = useDebounce(filterText, 150);

  // We can consider simplifying the filters by applying them in a single function, where we apply each filter in a single pass
  // Will need to change the existing filter function to just accept one column, and return a boolean
  useEffect(() => {
    if (!categoryList?.length || (!selectedCategory && !isFiltering)) {
      // Allow search to work when there is no field selected
      setList([]);
      return;
    }

    // Applied to the search branch too, so a stale boundary column from an
    // older session cannot resurface as a searchable row either.
    //
    // EVA-10: the store's `allColumns` is flattened from the UNGATED
    // categoryList, so search would resurface a flag-disabled category's
    // fields even when the category itself is hidden. Derive the searchable
    // set from the gated category list instead — keyed on the CONTAINING
    // category's name, like every other field-group gate, so browse and
    // search agree (a focus-field column keeps its origin `group`, e.g. a
    // "Header" column living under the unflagged "Org Focus Fields" stays
    // searchable when "Header Fields" is disabled). Closes the bypass for
    // every consumer of this hook (Select Field popover, Python field
    // picker) in one place.
    //
    // EVA-148: search flattens every category, so it would otherwise surface
    // the columns of a job hidden after the subscription lapsed.
    const isCategoryEnabled = (name: string) => {
      const flag = getFieldGroupFlag(name);
      return !flag || hasFeature(flag);
    };
    const searchableColumns = withVisibleOrgGeoColumns(
      categoryList ?? [],
      visibleJobNames
    )
      .filter((category) => isCategoryEnabled(category.name))
      .flatMap((category) => category.columns ?? []);
    // A saved focus field restores its category from the ungated list, so the
    // browse path needs the same gate or a disabled category stays browsable.
    const browseColumns =
      selectedCategory && isCategoryEnabled(selectedCategory.name)
        ? withVisibleOrgGeoColumns([selectedCategory], visibleJobNames)[0].columns ?? []
        : [];
    const columns = withoutShapefileBoundaryColumns(
      isFiltering ? searchableColumns : browseColumns
    );

    // props is only set when used from a chart axis context — skip type filtering otherwise (e.g. Python field picker)
    const typeColumns = props
      ? filterByType(props.dataTypeFilters, columns, props.chartType, props.axisType)
      : columns;

    const productColumns =
      selectedProduct?.length && hasProductFields && !isFiltering
        ? filterByProduct(selectedProduct, typeColumns)
        : typeColumns;

    const metadataColumns = !isFiltering
      ? filterByMetadata(selectedMetadataFilters, productColumns)
      : productColumns;

    // Consider that everything before this, will include the filters
    const filteredColumns = isFiltering
      ? filterByText(debouncedFilterText, metadataColumns)
      : metadataColumns;

    const authorizedByForecastColumns =
      selectedCategory?.name === "Performance"
        ? authorizeColumnsByForecast(filteredColumns, isForecastToggleOn)
        : filteredColumns;

    setList(authorizedByForecastColumns);
  }, [
    props?.dataTypeFilters,
    props?.chartType,
    categoryList,
    visibleJobNames,
    hasProductFields,
    selectedCategory,
    selectedProduct,
    selectedMetadataFilters,
    debouncedFilterText,
    isForecastToggleOn,
    hasFeature
  ]);

  return list;
};

export default useColumns;
